import Button, {ButtonGroup} from '@pinecast/common/Button';
import {toMediumDate} from '@pinecast/common/dates';
import {formatCurrency} from '@pinecast/common/helpers/format';
import {InlineSkeleton} from '@pinecast/common/Skeleton';
import {useConfirmBox} from '@pinecast/common/Stage';
import SegmentedStats from '@pinecast/db-spa/SegmentedStats';
import type {QueryResult} from '@pinecast/gql';
import {useManagedMutation} from '@pinecast/gql';
import type {
  CancelSubscription,
  CancelSubscriptionVariables,
} from '@pinecast/gql/__generated_tj__/CancelSubscription';
import type {
  ReactivateSubscription,
  ReactivateSubscriptionVariables,
} from '@pinecast/gql/__generated_tj__/ReactivateSubscription';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import type {RecurringDonationSupplemental} from '@pinecast/gql/__generated_tj__/RecurringDonationSupplemental';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';
import gql from 'graphql-tag';
import * as React from 'react';

const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($slug: ID!) {
    cancelSubscription(podcastSlug: $slug) {
      id
      willCancel
      nextInvoiceDate
    }
  }
`;
const REACTIVATE_SUBSCRIPTION = gql`
  mutation ReactivateSubscription($slug: ID!) {
    reactivateSubscription(podcastSlug: $slug) {
      id
      willCancel
      nextInvoiceDate
    }
  }
`;

const messages = defineMessages({
  button_cancel: {
    id: 'tj-spa.RecurringDonationHeader.button.cancel',
    description: 'Button to cancel an ongoing subscription',
    defaultMessage: 'End subscription…',
  },
  button_reactivate: {
    id: 'tj-spa.RecurringDonationHeader.button.reactivate',
    description: 'Button to reactivate a canceled subscription',
    defaultMessage: 'Set to renew…',
  },
  button_update: {
    id: 'tj-spa.RecurringDonationHeader.button.update',
    description: 'Button to update an ongoing subscription',
    defaultMessage: 'Change amount',
  },

  confirmCancelTitle: {
    id: 'tj-spa.RecurringDonationHeader.confirmCancelTitle',
    description:
      'Title of the confirmation box when cancelling a podcast subscription',
    defaultMessage: 'Confirm cancelation',
  },
  confirmCancelCopy: {
    id: 'tj-spa.RecurringDonationHeader.confirmCancelCopy',
    description:
      'Copy of the confirmation box when cancelling a podcast subscription',
    defaultMessage:
      'Canceling will stop you from being charged again for this show.',
  },
  confirmCancelCopyPrivate: {
    id: 'tj-spa.RecurringDonationHeader.confirmCancelCopy.private',
    description:
      'Copy of the confirmation box when cancelling a podcast subscription for shows with eligible private content',
    defaultMessage:
      "You'll lose access to private content for the show at the end of the current billing period.",
  },

  confirmReactivateTitle: {
    id: 'tj-spa.RecurringDonationHeader.confirmReactivateTitle',
    description:
      'Title of the confirmation box when reactivating a podcast subscription',
    defaultMessage: 'Renew subscription',
  },
  confirmReactivateCopy: {
    id: 'tj-spa.RecurringDonationHeader.confirmReactivateCopy',
    description:
      'Copy of the confirmation box when reactivating a podcast subscription',
    defaultMessage:
      'Renewing your subscription will cause your subscription to begin regularly billing you at the end of the current billing period.',
  },

  stat_started: {
    id: 'tj-spa.RecurringDonationHeader.stat_started',
    description: 'Header for when a subscription was started',
    defaultMessage: 'Started',
  },
  stat_nextCharge: {
    id: 'tj-spa.RecurringDonationHeader.stat_nextCharge',
    description: 'Header for when a subscription will bill next',
    defaultMessage: 'Next charge',
  },
  stat_nextCharge_noBilling: {
    id: 'tj-spa.RecurringDonationHeader.stat_nextCharge.noBilling',
    description: 'Notice when the podcast will not re-bill',
    defaultMessage: 'None',
  },
  stat_amount: {
    id: 'tj-spa.RecurringDonationHeader.stat_amount',
    description: 'Header for a podcast subscription amount',
    defaultMessage: 'Amount',
  },
});

type Props = {
  subscription: RecurringDonation_tipJarSubscription;
  supplemental: QueryResult<RecurringDonationSupplemental>;
};

const RecurringDonationHeader = ({subscription, supplemental}: Props) => {
  const cancelMutation = useManagedMutation<
    CancelSubscription,
    CancelSubscriptionVariables
  >(CANCEL_SUBSCRIPTION, {
    variables: {slug: subscription.podcast.slug},
  });
  const reactivateMutation = useManagedMutation<
    ReactivateSubscription,
    ReactivateSubscriptionVariables
  >(REACTIVATE_SUBSCRIPTION, {
    variables: {slug: subscription.podcast.slug},
  });

  const confirm = useConfirmBox();
  const handleCancel = React.useCallback(async () => {
    await confirm(
      <FormattedMessage {...messages.confirmCancelTitle} />,
      <>
        <FormattedMessage {...messages.confirmCancelCopy} />
        {subscription.eligibleForPrivateContent &&
          subscription.podcast.hasPrivateEpisodes && (
            <>
              {' '}
              <FormattedMessage {...messages.confirmCancelCopyPrivate} />
            </>
          )}
      </>,
    );

    await cancelMutation.action();
  }, [
    cancelMutation,
    confirm,
    subscription.eligibleForPrivateContent,
    subscription.podcast.hasPrivateEpisodes,
  ]);
  const handleReactivate = React.useCallback(async () => {
    await confirm(
      <FormattedMessage {...messages.confirmReactivateTitle} />,
      <FormattedMessage {...messages.confirmReactivateCopy} />,
    );

    await reactivateMutation.action();
  }, [reactivateMutation, confirm]);

  return (
    <>
      <SegmentedStats
        stats={[
          {
            label: <FormattedMessage {...messages.stat_amount} />,
            value: formatCurrency(subscription.amount, subscription.currency),
          },
          {
            label: <FormattedMessage {...messages.stat_started} />,
            value: toMediumDate(subscription.created),
          },
          !supplemental.error &&
            (supplemental.loading || supplemental.data.tipJarSubscription) && {
              label: <FormattedMessage {...messages.stat_nextCharge} />,
              value: supplemental.loading ? (
                <InlineSkeleton width="150px" />
              ) : supplemental.data.tipJarSubscription!.willCancel ? (
                <FormattedMessage {...messages.stat_nextCharge_noBilling} />
              ) : (
                toMediumDate(
                  supplemental.data.tipJarSubscription!.nextInvoiceDate,
                )
              ),
            },
        ]}
      />
      {supplemental.data && (
        <ButtonGroup style={{marginTop: '12px'}}>
          <></>
          {supplemental.data.tipJarSubscription!.willCancel ? (
            <Button
              onClick={handleReactivate}
              pending={reactivateMutation.pending}
            >
              <FormattedMessage {...messages.button_reactivate} />
            </Button>
          ) : (
            <>
              <Button onClick={handleCancel} pending={cancelMutation.pending}>
                <FormattedMessage {...messages.button_cancel} />
              </Button>
              <Button href={`/jar/${subscription.podcast.slug}`}>
                <FormattedMessage {...messages.button_update} />
              </Button>
            </>
          )}
        </ButtonGroup>
      )}
    </>
  );
};

export default RecurringDonationHeader;
